import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { getPriorityAnnouncement, markAllAnnouncementsRead, markAnnouncementRead } from '../services/announcementService';
import { Announcement } from '../types/Announcement';
import { announcementIds, formatAnnouncementDate, prepareAnnouncementHtml, validAnnouncementRumuz } from '../utils/announcement';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useAuth } from '../contexts/AuthContext';
import AnnouncementHtml from './AnnouncementHtml';

export default function PriorityAnnouncement() {
  const queryClient = useQueryClient();
  const { userData } = useAuth();
  const canMarkAll = userData?.MarkAllNotificationsAsRead === true;
  const [hiddenId, setHiddenId] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [markAll, setMarkAll] = useState(false);

  const { data } = useQuery({
    queryKey: ['priority-announcement'],
    queryFn: getPriorityAnnouncement,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const announcement = data && data.Id !== hiddenId ? data : null;
  useScrollPrevention(announcement != null);

  useEffect(() => {
    setMarkAll(false);
  }, [announcement?.Id]);

  const confirm = async () => {
    if (!announcement?.Id || pending) return;

    const markEveryUnread = canMarkAll && markAll;
    setPending(true);
    setError('');

    try {
      if (markEveryUnread) {
        const cached = queryClient.getQueryData<Announcement[]>(['announcements']);
        await markAllAnnouncementsRead(announcementIds(cached, announcement.Id));
        setHiddenId(announcement.Id);
        queryClient.setQueryData(['priority-announcement'], null);
      } else {
        await markAnnouncementRead(announcement.Id);
        setHiddenId(announcement.Id);
        queryClient.setQueryData(['priority-announcement'], null);
        await queryClient.invalidateQueries({ queryKey: ['priority-announcement'] });
      }
    } catch (confirmError) {
      console.error('Öncelikli duyuru okundu işaretlenemedi:', confirmError);
      setError('Okundu işaretlenemedi. Tekrar deneyin.');
    } finally {
      setPending(false);
    }
  };

  return (
    <AnimatePresence>
      {announcement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[60] overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="priority-announcement-title"
        >
          <div className="flex min-h-full items-center justify-center px-2 py-4 sm:p-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
              className="relative w-full mx-2 sm:mx-0 sm:my-8 sm:max-w-lg overflow-hidden rounded-xl border border-white/50 dark:border-gray-600/50 bg-gradient-to-br from-white/95 via-white/92 to-white/90 dark:from-gray-700/95 dark:via-gray-750/93 dark:to-gray-800/95 text-left shadow-2xl"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-violet-600 dark:text-violet-300">
                    Duyuru
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                    {formatAnnouncementDate(announcement.Tarih)}
                  </p>
                </div>

                <h2 id="priority-announcement-title" className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {announcement.Baslik}
                </h2>

                {(prepareAnnouncementHtml(announcement.Duyuru) || validAnnouncementRumuz(announcement.DosyaRumuz)) && (
                  <div className="max-h-[50vh] overflow-y-auto pr-1">
                    <AnnouncementHtml
                      html={announcement.Duyuru}
                      servisBulteniMi={announcement.ServisBulteniMi}
                      dosyaRumuz={announcement.DosyaRumuz}
                    />
                  </div>
                )}

                {error && (
                  <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                )}

                {canMarkAll && (
                  <label className="mt-6 flex items-start gap-3 cursor-pointer border-t border-gray-200 pt-4 text-sm text-gray-700 dark:text-gray-200 dark:border-gray-600">
                    <input
                      type="checkbox"
                      checked={markAll}
                      onChange={(event) => setMarkAll(event.target.checked)}
                      disabled={pending}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Tüm duyuruları okundu olarak işaretle</span>
                  </label>
                )}

                <button
                  type="button"
                  onClick={confirm}
                  disabled={pending}
                  autoFocus
                  className="mt-6 w-full rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:from-blue-600 hover:to-indigo-700 disabled:opacity-60"
                >
                  {pending ? 'İşaretleniyor...' : 'Okudum'}
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
